import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const INACTIVITY_TIMEOUT = 8 * 60 * 60 * 1000; // 8 hours

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [justLoggedIn, setJustLoggedIn] = useState(false);
  const timerRef = useRef(null);

  const logout = useCallback((expired = false) => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    if (expired) setSessionExpired(true);
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const resetInactivityTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (user) {
      timerRef.current = setTimeout(() => logout(true), INACTIVITY_TIMEOUT);
    }
  }, [user, logout]);

  // Set up inactivity listeners
  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'mousemove'];
    const handler = () => resetInactivityTimer();
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    resetInactivityTimer();

    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [user, resetInactivityTimer]);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          // Check URL for expired flag
          const params = new URLSearchParams(window.location.search);
          if (params.get('expired') === '1') {
            setSessionExpired(true);
            window.history.replaceState({}, '', window.location.pathname);
          }
        } catch (error) {
          console.error('Auth error:', error);
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
          if (error?.response?.status === 401) {
            setSessionExpired(true);
          }
        }
      } else {
        // Check URL for expired flag even without token
        const params = new URLSearchParams(window.location.search);
        if (params.get('expired') === '1') {
          setSessionExpired(true);
          window.history.replaceState({}, '', window.location.pathname);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (username, password) => {
    const response = await axios.post(`${API}/auth/login`, { username, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    setSessionExpired(false);
    setJustLoggedIn(true);
    return userData;
  };

  const clearJustLoggedIn = useCallback(() => setJustLoggedIn(false), []);

  const value = {
    user,
    token,
    loading,
    login,
    logout: () => logout(false),
    isAuthenticated: !!user,
    sessionExpired,
    setSessionExpired,
    justLoggedIn,
    clearJustLoggedIn,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
